import { FC, ComponentPropsWithoutRef, PropsWithChildren } from 'react';
import classnames from 'classnames';

import { Text as MagritteText } from '@hh.ru/magritte-ui';
import { CheckCircleOutlinedSize16, CheckOutlinedSize16, CrossOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import Text from 'bloko/blocks/text';

import BlokoIconReplaceContainer from 'src/components/BlokoIconReplaceContainer';
import FormatMoney from 'src/components/FormatMoney';
import { CurrencyType } from 'src/models/currencies.types';
import { NON_BREAKING_SPACE } from 'src/utils/constants/symbols';

export const GbbColumns: FC<PropsWithChildren> = ({ children }) => <div className="gbb-columns">{children}</div>;

const GbbFeatureMagritte: FC<PropsWithChildren> = ({ children }) => (
    <div className={classnames('gbb-feature')}>
        <MagritteText typography="label-3-regular">{children}</MagritteText>
    </div>
);

const GbbFeature: FC<
    {
        bad?: boolean;
    } & PropsWithChildren
> = ({ bad, children }) => (
    <div className={classnames('gbb-feature', { 'gbb-feature_bad': bad })}>
        <Text>{children}</Text>
    </div>
);

export const GbbGoodFeature: FC<PropsWithChildren> = ({ children }) => (
    <GbbFeature>
        <div className="gbb-feature-icon">
            <BlokoIconReplaceContainer>
                <CheckCircleOutlinedSize16 initialColor="positive" />
            </BlokoIconReplaceContainer>
        </div>
        {children}
    </GbbFeature>
);

export const GbbGoodFeatureMagritte: FC<PropsWithChildren> = ({ children }) => (
    <GbbFeatureMagritte>
        <div className="gbb-feature-icon">
            <CheckOutlinedSize16 initialColor="accent" />
        </div>
        {children}
    </GbbFeatureMagritte>
);

export const GbbBadFeature: FC<PropsWithChildren> = ({ children }) => (
    <GbbFeature bad>
        <div className="gbb-feature-icon">
            <BlokoIconReplaceContainer>
                <CrossOutlinedSize16 initialColor="secondary" />
            </BlokoIconReplaceContainer>
        </div>
        {children}
    </GbbFeature>
);

export const GbbPrice: FC<{ price: number; currency: CurrencyType }> = ({ price, currency }) => (
    <span className="gbb-price">
        <FormatMoney
            currency={currency}
            inCents={true}
            groupSeparator={NON_BREAKING_SPACE}
            render={(amount, currencyNode) => (
                <>
                    <span data-qa="money__amount">{amount}</span>
                    {NON_BREAKING_SPACE}
                    <span data-qa="money__currency">{currencyNode}</span>
                </>
            )}
        >
            {price}
        </FormatMoney>
    </span>
);

interface GbbItemProps extends ComponentPropsWithoutRef<'div'> {
    highlighted?: boolean;
    advantaged?: boolean;
    vertical?: boolean;
    flat?: boolean;
    decorate?: boolean;
}

export const GbbItem: FC<GbbItemProps & PropsWithChildren> = ({
    children,
    highlighted,
    advantaged,
    vertical,
    flat,
    decorate,
    ...divProps
}) => (
    <div
        className={classnames('gbb-item', {
            'gbb-item_highlighted': highlighted,
            'gbb-item_advantaged': advantaged,
            'gbb-item_vertical': vertical,
            'gbb-item_flat': flat,
            'gbb-item_decorate': decorate,
        })}
        {...divProps}
    >
        {children}
    </div>
);

export const GbbDescription: FC<PropsWithChildren> = ({ children }) => <div>{children}</div>;

export const GbbHeader: FC<
    {
        centered?: boolean;
    } & PropsWithChildren
> = ({ children, centered = false }) => (
    <div className={classnames({ 'gbb-header_centered': centered })}>{children}</div>
);

export const GbbDivider: FC = () => <div className="gbb-divider" />;

export const GbbControls: FC<PropsWithChildren> = ({ children }) => <div className="gbb-controls">{children}</div>;

export const GbbAdvantage: FC<PropsWithChildren> = ({ children }) => (
    <div className="gbb-advantage-placeholder">{children && <div className="gbb-advantage">{children}</div>}</div>
);
